import React, { useEffect, useState } from 'react';
import {
  Editor,
  EditorState,
  RichUtils,
  DraftHandleValue,
  ContentState
} from 'draft-js';
import styled from 'styled-components';
import 'draft-js/dist/Draft.css';

import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';

// Styled components (Container, Title, EditorContainer, Toolbar, Button)
const Container = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  background-color: #f7f7f7;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;
const Title = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 20px;
`;
const EditorContainer = styled.div`
  border: 1px solid #ccc;
  border-radius: 8px;
  min-height: 250px;
  padding: 15px;
  background: #fff;
  box-shadow: inset 0 2px 5px rgba(0, 0, 0, 0.05);
  margin-bottom: 20px;
  cursor: text;
`;
const Toolbar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
`;
const Button = styled.button<{ active?: boolean }>`
  background-color: ${(props) => (props.active ? '#007bff' : '#f0f0f0')};
  border: none;
  border-radius: 4px;
  color: ${(props) => (props.active ? '#fff' : '#333')};
  cursor: pointer;
  font-size: 14px;
  margin: 0 5px;
  padding: 8px 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.active ? '#0056b3' : '#e0e0e0'};
  }
`;

interface NewsFormProps {
  formData: any;
  handleChange: (name: string, value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
}

const NewsForm: React.FC<NewsFormProps> = ({
  formData,
  handleChange,
  handleSubmit
}) => {
  // Create an EditorState using existing HTML content if available.
  const initializeEditorState = () => {
    if (formData && formData.content) {
      try {
        const contentState = stateFromHTML(formData.content);
        return EditorState.createWithContent(contentState);
      } catch (error) {
        console.error("Error converting HTML to EditorState", error);
        return EditorState.createEmpty();
      }
    }
    return EditorState.createEmpty();
  };

  const [editorState, setEditorState] = useState<EditorState>(
    initializeEditorState()
  );

useEffect(() => {
  const contentState = editorState.getCurrentContent();
  const html = stateToHTML(contentState);
  // Only update if the content is actually different.
  if (html !== formData.content) {
    handleChange('content', html);
  }
}, [editorState, formData.content, handleChange]);


  // Handle key commands (e.g., Cmd/Ctrl+B)
  const handleKeyCommand = (
    command: string,
    currentEditorState: EditorState
  ): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(currentEditorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const toggleInlineStyle = (style: string) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  const toggleBlockType = (blockType: string) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  const currentStyle = editorState.getCurrentInlineStyle();

  return (
    <Container>
      <Title>Content</Title>

      {/* Inline Styles Toolbar */}
      <Toolbar>
        <Button
          active={currentStyle.has('BOLD')}
          onClick={() => toggleInlineStyle('BOLD')}
        >
          Bold
        </Button>
        <Button
          active={currentStyle.has('ITALIC')}
          onClick={() => toggleInlineStyle('ITALIC')}
        >
          Italic
        </Button>
        <Button
          active={currentStyle.has('UNDERLINE')}
          onClick={() => toggleInlineStyle('UNDERLINE')}
        >
          Underline
        </Button>
      </Toolbar>

      {/* Block Styles Toolbar */}
      <Toolbar>
        <Button onClick={() => toggleBlockType('header-one')}>Header</Button>
        <Button onClick={() => toggleBlockType('unordered-list-item')}>
          Bullet List
        </Button>
        <Button onClick={() => toggleBlockType('ordered-list-item')}>
          Numbered List
        </Button>
      </Toolbar>

      {/* Editor Container */}
      <EditorContainer onClick={() => { /* Optionally focus the editor here */ }}>
        <Editor
          editorState={editorState}
          handleKeyCommand={handleKeyCommand}
          onChange={setEditorState}
          placeholder="Start typing..."
        />
      </EditorContainer>
    </Container>
  );
};

export default NewsForm;
